import type { GetListResponse } from "libs/api";

export enum EAiAssistantChannel {
  WhatsApp = "whatsapp",
  InAppSupport = "in_app_support"
}

export enum EAiAssistantRuleKind {
  Required = "required",
  Boundary = "boundary",
  Knowledge = "knowledge"
}

export interface AiAssistant {
  id: string;
  channel: EAiAssistantChannel;
  name: string;
  isActive: boolean;
  // Per-assistant switch for lessons; off until someone turns it on.
  lessonsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AiAssistantRule {
  id: string;
  assistantId: string;
  kind: EAiAssistantRuleKind;
  title: string;
  body: string;
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiAssistantTool {
  toolKey: string;
  label: string;
  description: string;
  channels: EAiAssistantChannel[];
  isEnabled: boolean;
}

export interface AiAssistantExpertSettings {
  id: string;
  systemPrompt: string;
}

// The actual prompt the agent runs with (floor + enabled rules), read-only.
export interface AiAssistantEffectivePrompt {
  assistantId: string;
  channel: EAiAssistantChannel;
  systemPrompt: string;
}

export interface AiAssistantStats {
  convos: number;
  resolvedRate: number;
  handoffRate: number;
}

// How the sandbox handled a tool the agent called.
export enum ESandboxToolExecMode {
  Executed = "executed",
  Simulated = "simulated",
  Terminal = "terminal"
}

export interface SandboxSession {
  sessionId: string;
  assistantId: string;
  channel: EAiAssistantChannel;
  createdAt: string;
}

export interface SandboxToolTrace {
  round: number;
  toolName: string;
  input: Record<string, unknown>;
  mode: ESandboxToolExecMode;
  isError: boolean;
  resultPreview: string;
  note?: string;
}

export interface SandboxReply {
  kind: "reply" | "escalate" | "exhausted";
  text?: string;
  reason?: string;
}

export interface SandboxUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface SandboxMessageResult {
  reply: SandboxReply;
  rounds: number;
  toolTrace: SandboxToolTrace[];
  usage: SandboxUsage;
}

// AI lessons: an admin turns a bad WhatsApp reply into a short guidance that is
// added to the assistant's instructions only in similar situations. Same
// string values as the backend enums.
export enum EAiLessonStatus {
  Draft = "draft",
  Tested = "tested",
  Active = "active",
  Paused = "paused",
  Archived = "archived"
}

export enum EAiLessonCategory {
  Tone = "tone",
  Behaviour = "behaviour"
}

export enum EAiLessonReplayStatus {
  NotRun = "not_run",
  Passed = "passed",
  Failed = "failed"
}

export enum EAiLessonTag {
  FirstContact = "first_contact",
  ConversationOngoing = "conversation_ongoing",
  ResumedSameDay = "resumed_same_day",
  ResumedOtherDay = "resumed_other_day",
  AdLead = "ad_lead",
  CartOpen = "cart_open",
  Checkout = "checkout",
  AwaitingPayment = "awaiting_payment",
  OpenOrder = "open_order",
  Reschedule = "reschedule",
  Voice = "voice",
  Image = "image",
  TalksPrice = "talks_price",
  TalksDelivery = "talks_delivery",
  TalksPayment = "talks_payment",
  ComplaintOrHuman = "complaint_or_human",
  Refusal = "refusal",
  LastAiWasQuestion = "last_ai_was_question",
  LangFr = "lang_fr",
  LangEn = "lang_en"
}

export type AiLessonMessageType = "text" | "voice" | "image";

export interface AiLessonTriggers {
  tags: EAiLessonTag[];
  keywords: string[];
  messageTypes: AiLessonMessageType[];
}

export interface AiLessonVariants {
  a: string;
  b: string;
  chosen: "a" | "b" | null;
  edited: boolean;
  model: string | null;
  generatedAt: string | null;
}

// rule = a guidance-validator rule id ("amount", "url"...) or
// "no_discriminating_tag"; match = the words that triggered it.
export interface AiLessonGuidanceIssue {
  rule: string;
  match: string;
}

// Replay before activation: the lesson's source turn is replayed with the
// lesson and without it (the control), 1 to 3 times, nothing sent to the
// customer. Same shapes as the backend's IAiLessonReplayResult.
export type AiLessonReplayApproximateReason =
  | "no_snapshot"
  | "no_context"
  | "no_workflow"
  | "media_unread";

export type AiLessonReplayErrorCode =
  | "seed_failed"
  | "lesson_not_injected"
  | "unexpected";

export type AiLessonReplayVerdict = "passed" | "failed";

export type AiLessonReplayOutcome =
  | "reply"
  | "silent"
  | "escalated"
  | "exhausted"
  | "error";

export type AiLessonReplayToolMode = "executed" | "simulated" | "terminal";

export interface AiLessonReplayToolCall {
  round: number;
  toolName: string;
  // A long input arrives as { _cut: "<the start of its JSON>" }.
  input: Record<string, unknown>;
  isError: boolean;
  resultPreview: string;
  mode: AiLessonReplayToolMode;
  note: string | null;
}

export interface AiLessonReplayRun {
  // Synthetic ("replay-<uuid>"), never the real message id.
  replayTurnId: string;
  // The reply the customer would have got; null when there is none.
  text: string | null;
  outcome: AiLessonReplayOutcome;
  outcomeReason: string | null;
  rounds: number;
  repairs: string[];
  // Visual tools that delivered (no error), in call order.
  visualTools: string[];
  browsePage: boolean;
  toolTrace: AiLessonReplayToolCall[];
  traceTruncated: boolean;
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheCreationTokens: number;
  };
}

export interface AiLessonReplayResult {
  schema: 1;
  status: "running" | "done" | "error";
  error: AiLessonReplayErrorCode | null;
  startedAt: string;
  finishedAt: string | null;
  startedByAdminId: string;
  // The guidance replayed, and the lesson version it belonged to.
  lessonVersion: number;
  guidance: string;
  turnMessageId: string;
  conversationId: string;
  runsRequested: number;
  mode: "replay_core" | "production_loop";
  fidelity: "full" | "partial" | "reconstructed";
  approximate: boolean;
  approximateReasons: AiLessonReplayApproximateReason[];
  // null = no recorded hash to compare.
  systemHashChanged: boolean | null;
  situationTags: string[];
  lessonMatchesTurn: boolean | null;
  // What the customer really got.
  before: {
    text: string | null;
    cards: string[];
    outcome: string | null;
    outcomeReason: string | null;
  };
  runs: {
    index: number;
    withLesson: AiLessonReplayRun;
    control: AiLessonReplayRun;
  }[];
  totalRounds: number;
  costUsdEstimate: number;
  verdict: AiLessonReplayVerdict | null;
  judgedByAdminId: string | null;
  judgedAt: string | null;
  // Set only by the list route, which leaves the runs out.
  runsOmitted?: true;
}

export interface AiLesson {
  id: string;
  assistantId: string;
  status: EAiLessonStatus;
  isEnabled: boolean;
  category: EAiLessonCategory;
  title: string;
  situation: string;
  badReply: string;
  correctedReply: string;
  guidance: string;
  variants: AiLessonVariants | null;
  triggers: AiLessonTriggers;
  sourceChannel: "whatsapp" | null;
  sourceConversationId: string | null;
  sourceTurnMessageId: string | null;
  sourceMessageId: string | null;
  replayStatus: EAiLessonReplayStatus;
  lastReplayAt: string | null;
  // A backend that predates the replay always sends null here.
  lastReplayResult: AiLessonReplayResult | null;
  usageCount: number;
  lastUsedAt: string | null;
  version: number;
  createdByAdminId: string | null;
  updatedByAdminId: string | null;
  createdAt: string;
  updatedAt: string;
}

// For the whole assistant, whatever the status filter and search.
export interface AiLessonCounts {
  draft: number;
  tested: number;
  active: number;
  paused: number;
  archived: number;
}

export type AiLessonListResponse = GetListResponse<AiLesson> & {
  counts: AiLessonCounts;
};

export interface AiLessonDraft {
  title: string;
  situation: string;
  category: EAiLessonCategory;
  variantA: string;
  variantB: string;
  guidance: string;
  tags: string[];
  keywords: string[];
  warnings: AiLessonGuidanceIssue[];
  model: string;
  usage: { inputTokens: number; outputTokens: number };
}

export interface AiLessonFirePreviewSample {
  turnMessageId: string;
  conversationId: string;
  createdAt: string;
  // The customer's words, cut by the backend (FIRE_PREVIEW.EXCERPT_LENGTH).
  inboundText: string;
  // The situations that turn showed, from its words only (cart, orders and
  // ads of that time read as absent). Not displayed yet.
  tags: string[];
  score: number;
}

export interface AiLessonFirePreview {
  lessonId: string;
  lessonVersion: number;
  days: number;
  scannedTurns: number;
  matchedTurns: number;
  ratio: number;
  isUpperBound: boolean;
  assumedTags: string[];
  tooBroad: boolean;
  truncated: boolean;
  samples: AiLessonFirePreviewSample[];
}
